import { useEffect, useState } from 'react';
import Sheet from '../components/Sheet';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import { PRIMARY } from '../components/CaptureSheets';
import { useApp } from '../lib/AppContext';
import AvoidFoods from './AvoidFoods';
import { DAYS, MEALS, MEAL_NAMES, PRESETS, avoidLine } from './usePair';

const ROW = 'flex min-h-14 w-full items-center gap-3 rounded-[16px] border border-line px-4 text-left';
const vnDay = (iso) => { const [, m, d] = iso.split('-'); return `${Number(d)}/${Number(m)}`; };

// Two overlapping avatars, "H L · Nấu chung" in the menu's header; opens the manage sheet (⑦).
export function AvatarPair({ pair, onClick, label = true }) {
  return (
    <button type="button" onClick={onClick} aria-label={`Nấu chung với ${pair.partner.name}`}
      className="flex h-11 items-center gap-2 rounded-full border border-line bg-base-100 pl-1.5 pr-3 font-semibold">
      <span className="flex -space-x-2">
        <Avatar name={pair.me.name} url={pair.me.avatar_url} size={30} />
        <Avatar name={pair.partner.name} url={pair.partner.avatar_url} size={30} className="ring-2 ring-base-100" />
      </span>
      {label && <span className="text-sm">Nấu chung</span>}
    </button>
  );
}

// ④ "Bữa nào ăn chung?": quick presets and a 7 × 3 grid, each cell Chung or Riêng (snacks are always own).
export function SlotPicker({ open, pair, intro, saveLabel, onClose, onSave }) {
  const [slots, setSlots] = useState(pair?.shared_slots || PRESETS[1].slots);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setSlots(pair?.shared_slots || PRESETS[1].slots); setError(null); } }, [open, pair]);
  const has = (slot) => slots.includes(slot);
  const toggle = (slot) => setSlots((s) => (s.includes(slot) ? s.filter((x) => x !== slot) : [...s, slot]));
  const preset = PRESETS.find((p) => p.slots.length === slots.length && p.slots.every(has));
  const avoid = pair ? avoidLine([pair.me, pair.partner]) : '';
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(slots);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title="Bữa nào ăn chung?">
      <div className="flex flex-col gap-4">
        {intro && <p className="rounded-[14px] bg-[#e3f1e9] p-3 text-sm text-[#17201b]">{intro}</p>}
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.id} type="button" aria-pressed={preset?.id === p.id} onClick={() => setSlots(p.slots)}
              className={`h-9 rounded-full border px-3 text-sm font-semibold ${preset?.id === p.id ? 'border-neutral bg-base-100 ring-2 ring-neutral' : 'border-line bg-base-100'}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-[2.25rem_repeat(3,1fr)] items-center gap-1.5 rounded-[16px] bg-base-100 p-3">
          <span />
          {MEALS.map((m) => <span key={m} className="text-center text-xs font-semibold text-muted">{MEAL_NAMES[m]}</span>)}
          {DAYS.map((d, i) => (
            <div key={d} className="contents">
              <span className="text-sm font-bold">{d}</span>
              {MEALS.map((m) => {
                const slot = `${i + 1}:${m}`;
                return (
                  <button key={slot} type="button" aria-pressed={has(slot)} aria-label={`${d} ${MEAL_NAMES[m]}: ${has(slot) ? 'Chung' : 'Riêng'}`}
                    onClick={() => toggle(slot)}
                    className={`flex h-9 items-center justify-center gap-1 rounded-[10px] border text-xs font-semibold ${has(slot) ? 'border-[#17804f] bg-[#17804f] text-white' : 'border-line text-muted'}`}>
                    {has(slot) ? <><Icon name="check" className="size-3" /> Chung</> : 'Riêng'}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted">Bữa <b>ăn chung</b>: cùng món, mỗi người một khẩu phần. Bữa <b>riêng</b>: ai theo thực đơn người nấy. Ăn vặt luôn riêng.</p>
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <div className="flex flex-col items-center gap-1.5">
          <button type="button" className={PRIMARY} disabled={busy || slots.length === 0} onClick={save}>
            {busy && <span className="loading loading-spinner loading-sm" />} {saveLabel || `Lưu bữa ăn chung · ${slots.length} bữa`}
          </button>
          {avoid && <p className="text-xs text-muted">AI chọn món hợp cả hai: {avoid}</p>}
        </div>
      </div>
    </Sheet>
  );
}

// ⑤ One shared meal's portions: each ingredient for "Bạn" and the partner, then each person's kcal and protein.
export function PortionTable({ pair }) {
  if (!pair?.portions) {
    return <p className="rounded-[14px] bg-base-200 p-3 text-xs text-muted">Bữa ăn chung với {pair?.partner_name}. Món này chưa có danh sách nguyên liệu nên chưa chia được khẩu phần.</p>;
  }
  const { me, partner } = pair.portions;
  const find = (list, name) => list.find((i) => i.name === name);
  const protein = me.filter((i) => i.kind === 'protein').map((i) => `${i.name.toLowerCase()} ${i.amount} g / ${find(partner, i.name)?.amount} g`);
  return (
    <div className="flex flex-col gap-2">
      <table className="w-full rounded-[14px] bg-base-200 text-sm">
        <thead>
          <tr className="text-xs text-muted">
            <th className="px-3 pb-1 pt-2 text-left font-semibold">Khẩu phần</th>
            <th className="px-2 pb-1 pt-2 text-right font-semibold text-brand-text">Bạn</th>
            <th className="px-3 pb-1 pt-2 text-right font-semibold text-[#b2456a]">{pair.partner_name}</th>
          </tr>
        </thead>
        <tbody>
          {me.map((item) => (
            <tr key={item.name} className="border-t border-line/60">
              <td className="px-3 py-1.5">{item.name}</td>
              <td className="tabular px-2 py-1.5 text-right font-semibold">{item.amount} {item.unit}</td>
              <td className="tabular px-3 py-1.5 text-right font-semibold">{find(partner, item.name)?.amount ?? '—'} {item.unit}</td>
            </tr>
          ))}
          <tr className="border-t border-line">
            <td className="px-3 py-2 font-semibold">Tổng</td>
            <td className="tabular px-2 py-2 text-right"><b>{pair.totals.me.calories} kcal</b><br /><span className="text-protein">{Math.round(pair.totals.me.protein_g)} g đạm</span></td>
            <td className="tabular px-3 py-2 text-right"><b>{pair.totals.partner.calories} kcal</b><br /><span className="text-protein">{Math.round(pair.totals.partner.protein_g)} g đạm</span></td>
          </tr>
        </tbody>
      </table>
      {protein.length > 0 && <p className="text-xs text-muted">Gợi ý: nấu chung một nồi, chia {protein.join(', ')}.</p>}
    </div>
  );
}

// ⑦ "Nấu chung": since when, both people with their foods to avoid, and the three actions.
export function ManageSheet({ open, pair, onClose, onSlots, onFoods, onLeave }) {
  if (!pair) return null;
  const people = [pair.me, pair.partner];
  return (
    <Sheet open={open} onClose={onClose} title="Nấu chung" badge={<span className="text-xs text-muted">từ {vnDay(pair.starts_on)} · {pair.meals_per_week} bữa/tuần</span>}>
      <div className="flex flex-col gap-3">
        <ul className="flex flex-col gap-2 rounded-[16px] bg-base-200 p-3">
          {people.map((p, i) => (
            <li key={p.name + i} className="flex items-center gap-3">
              <Avatar name={p.name} url={p.avatar_url} size={36} />
              <span className="min-w-0">
                <span className="block font-semibold">{p.name}{i === 0 ? ' (bạn)' : ''}</span>
                <span className="block text-xs text-muted">
                  {[p.inviter && 'Người mời', p.avoid_foods.length ? `Không ăn ${p.avoid_foods.join(', ').toLowerCase()}` : null].filter(Boolean).join(' · ') || 'Ăn được mọi món'}
                </span>
              </span>
            </li>
          ))}
        </ul>
        <button type="button" className={ROW} onClick={onSlots}>
          <Icon name="calendar" className="size-5" />
          <span className="flex-1"><span className="block font-semibold">Đổi bữa ăn chung</span><span className="block text-xs text-muted">{pair.can_edit ? 'Chọn lại bữa nào ăn cùng nhau' : 'Cần gói Pro'}</span></span>
        </button>
        <button type="button" className={ROW} onClick={onFoods}>
          <Icon name="x" className="size-5" />
          <span className="flex-1"><span className="block font-semibold">Món không ăn được của tôi</span><span className="block text-xs text-muted">Áp dụng cho các bữa ăn chung</span></span>
        </button>
        <button type="button" className={`${ROW} text-error`} onClick={onLeave}>
          <Icon name="chevronLeft" className="size-5" />
          <span className="flex-1"><span className="block font-semibold">Rời nấu chung</span><span className="block text-xs text-muted">Ai cũng rời được bất cứ lúc nào</span></span>
        </button>
      </div>
    </Sheet>
  );
}

// "Món không ăn được của tôi": saving swaps the shared dishes that have them (no AI credits).
export function FoodsSheet({ open, onClose, onSaved }) {
  const { api } = useApp();
  const [foods, setFoods] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) api.get('/profile').then((p) => setFoods(p.avoid_foods || [])).catch((e) => setError(e.message)); }, [open, api]);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await api.patch('/profile', { avoid_foods: foods });
      onSaved(p.auto_swapped || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title="Món không ăn được của tôi">
      <div className="flex flex-col gap-4">
        {foods ? <AvoidFoods value={foods} onChange={setFoods} /> : <div className="skeleton h-20 rounded-[16px]" />}
        <p className="text-xs text-muted">Món có những thứ này ở bữa ăn chung sẽ được đổi tự động, không tốn lượt AI.</p>
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <button type="button" className={PRIMARY} disabled={busy || !foods} onClick={save}>
          {busy ? <><span className="loading loading-spinner loading-sm" /> Đang đổi món…</> : 'Lưu'}
        </button>
      </div>
    </Sheet>
  );
}

// ⑧ "Rời nấu chung với …?": keep the dishes (default) or leave own shared meals empty.
export function LeaveSheet({ open, pair, onClose, onLeave }) {
  const [choice, setChoice] = useState('keep');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!pair) return null;
  const name = pair.partner.name;
  const leave = async () => {
    setBusy(true);
    setError(null);
    try {
      await onLeave(choice);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  const option = (id, title, text, tag) => (
    <button type="button" aria-pressed={choice === id} onClick={() => setChoice(id)}
      className={`flex items-start gap-3 rounded-[16px] border p-3 text-left ${choice === id ? 'border-[#17804f] bg-[#eef7f1]' : 'border-line'}`}>
      <span className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border-2 ${choice === id ? 'border-[#17804f]' : 'border-line'}`}>
        {choice === id && <span className="size-2.5 rounded-full bg-[#17804f]" />}
      </span>
      <span>
        <span className="flex items-center gap-2 font-semibold">{title}{tag && <span className="rounded-full bg-[#e3f1e9] px-2 py-0.5 text-[10px] font-bold text-[#17804f]">{tag}</span>}</span>
        <span className="block text-xs text-muted">{text}</span>
      </span>
    </button>
  );
  return (
    <Sheet open={open} onClose={onClose} title={`Rời nấu chung với ${name}?`}>
      <div className="flex flex-col gap-3">
        <p className="text-sm">Từ bữa tiếp theo, mỗi người một thực đơn riêng. Bữa đã ghi và ngày đã qua giữ nguyên. {name} sẽ nhận thông báo.</p>
        <p className="text-sm font-bold">Các bữa ăn chung còn lại trong tuần</p>
        {option('keep', 'Giữ các món', 'Mỗi người giữ bản sao các món với khẩu phần của mình, không ai bị trống thực đơn.', 'NÊN CHỌN')}
        {option('empty', 'Để trống, tôi tạo thực đơn mới', 'Chỉ áp dụng cho bạn. Tạo bằng AI tốn 1 lượt.')}
        <p className="text-xs text-muted">Danh sách đi chợ tách lại thành 2 danh sách riêng.</p>
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <button type="button" className="btn h-[54px] w-full rounded-2xl border-0 bg-[#b42318] text-base font-bold text-white" disabled={busy} onClick={leave}>
          {busy && <span className="loading loading-spinner loading-sm" />} Rời nấu chung
        </button>
        <button type="button" className="btn btn-ghost h-11 rounded-2xl" onClick={onClose}>Huỷ</button>
      </div>
    </Sheet>
  );
}
